import { Alert, AlertTitle, AlertDescription } from "./components/ui/alert";
import { Button } from "./components/ui/button";
import { Warning, ArrowClockwise, House } from "@phosphor-icons/react";
import { motion } from "framer-motion";

import { FallbackProps } from "react-error-boundary";

export const ErrorFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-secondary/5 to-destructive/5 flex items-center justify-center p-4">
      <motion.div 
        className="w-full max-w-md"
        initial={{ opacity: 0, y: 50, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6 }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Alert variant="destructive" className="mb-6 bg-destructive/10 border-destructive/20">
            <Warning size={20} />
            <AlertTitle className="text-lg">Oops! Something went wrong</AlertTitle>
            <AlertDescription className="text-base">
              We encountered an unexpected error. Our team has been notified and we're working to fix this issue.
            </AlertDescription>
          </Alert>
        </motion.div>
        
        <motion.div 
          className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl p-6 mb-6 shadow-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h3 className="font-semibold text-sm text-muted-foreground mb-3 flex items-center gap-2">
            <Warning size={16} />
            Error Details:
          </h3>
          <pre className="text-xs text-destructive bg-destructive/10 p-4 rounded-lg border border-destructive/20 overflow-auto max-h-32 font-mono">
            {error instanceof Error ? error.message : String(error)}
          </pre>
        </motion.div>
        
        <motion.div 
          className="space-y-3"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <Button 
            onClick={resetErrorBoundary} 
            className="w-full h-12 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary text-white font-semibold shadow-lg"
            size="lg"
          >
            <ArrowClockwise size={18} className="mr-2" />
            Try Again
          </Button>
          
          <Button 
            onClick={() => window.location.href = '/'} 
            variant="outline"
            className="w-full h-12"
            size="lg"
          >
            <House size={18} className="mr-2" />
            Go to Home
          </Button>
        </motion.div>

        <motion.div 
          className="text-center mt-6 text-sm text-muted-foreground"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          If this problem persists, please contact our support team.
        </motion.div>
      </motion.div>
    </div>
  );
};
